import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { PagesService } from 'src/app/services/pages/pages.service';
import { AdminService } from 'src/app/services/admin/admin.service';

import { environment } from '../../../../../environments/environment';
import { UploadComponent } from 'src/app/components/layout/file-manager/upload/upload.component';
import { TextEditorComponent } from "src/app/components/layout/text-editor/text-editor.component";
import { MetaFormModalComponent } from "src/app/components/layout/app-meta-modal/app-meta-modal.component";

@Component({
  selector: 'app-post-modal',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    UploadComponent,
    TextEditorComponent,
    MetaFormModalComponent
],
  templateUrl: './post-modal.component.html',
  styleUrl: './post-modal.component.css'
})
export class PostModalComponent implements OnInit {
  @Input() initialData: any = null;
  @Input() isPost: boolean = false;
  @Input() menuid: number = 161;
  @Output() save = new EventEmitter<any>();
  @Output() closeModal = new EventEmitter<void>();

  pageForm: FormGroup;
  selectedImage: string | null = null;
  gallery: string[] = [];
  galleryItems: any[] = []; // { media_id, url, pin, sort }
  // split for UI (stable arrays for cdkDropList)
  imageItemsArr: any[] = [];
  videoItemsArr: any[] = [];
  fileItemsArr: any[] = [];
  loadSave = false;
  editingPageId: number | null = null;

  currentLang = 'geo';
  originalSlug: string = '';

  domain = environment.apiDomain;

  @Input() selectedItems!: any[];

  isFullscreen = false;

  sorting = false;


  // Used in template: [uploadPath]="getListUploadPath()"
  public getListUploadPath(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `res/list/${year}/${month}`;
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private PagesService: PagesService,
    private ToastrService: ToastrService,
    private AdminService: AdminService
  ) {
    this.pageForm = this.fb.group({
      id: [null],
      lang: ['geo'],

      main_menu_page: [0],
      menu_page: [0],
      menu_id: [null],

      photogallery: [0],
      only_gallery: [0],

      slug: [''],
      title: [''],
      bold: [0],
      main_image: [''],
      content: [''],

      video_src: [''],
      video_image: [''],

      meta_keywords: [''],
      meta_description: [''],

      pin: [0],
      visible: [1],

      add_date: [null],
      election_id: [null],
      subscribe_id: [null],

      list_status: ['draft'],
      migrate_meetings: [null],

      gallery: this.fb.control([]),
      author: ['']
    });
  }

  onCheckboxChange(field: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.pageForm.patchValue({
      [field]: checked ? 1 : 0
    });
  }

  get allowedFiles(): string[] {
    const main = (this.pageForm.get('main_image')?.value as string) || '';
    const gallery = (this.pageForm.get('gallery')?.value as string[]) || [];

    const list = main ? [main, ...gallery] : [...gallery];
    // remove empties + duplicates
    return Array.from(new Set(list.filter((x) => !!x && String(x).trim().length > 0)));
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;

    const modal = document.querySelector('.custom-modal .modal-dialog');
    if (!modal) return;

    if (this.isFullscreen) {
      modal.classList.add('modal-fullscreen-custom');
    } else {
      modal.classList.remove('modal-fullscreen-custom');
    }
  }

  ngOnInit(): void {
    if (this.initialData) {
      this.fillForm(this.initialData);
    }
  }

 

  private fillForm(data: any) {
    this.pageForm.patchValue({
      id: data.id ?? null,
      lang: data.lang ?? 'geo',
      main_menu_page: data.main_menu_page ?? 0,
      menu_page: data.menu_page ?? 0,
      menu_id: data.menu_id ?? null,
      photogallery: data.photogallery ?? 0,
      only_gallery: data.only_gallery ?? 0,
      slug: data.slug ?? '',
      title: data.title ?? '',
      bold: data.bold ?? 0,
      main_image: data.main_image ?? null,
      content: this.addDomainToImageSrc(this.decodeHtml(data.content || '')),
      video_src: data.video_src ?? '',
      video_image: data.video_image ?? '',
      meta_keywords: data.meta_keywords ?? '',
      meta_description: data.meta_description ?? '',
      pin: data.pin ?? 0,
      visible: data.visible ?? 1,
      add_date: data.date ?? null,
      election_id: data.election_id ?? null,
      subscribe_id: data.subscribe_id ?? null,
      list_status: data.list_status ?? 'draft',
      migrate_meetings: data.migrate_meetings ?? null,
      gallery: data.gallery ?? [],
      author: data.author ?? null
    });

    this.originalSlug = data.slug ?? '';
    this.selectedImage = data.main_image ?? null;
    this.pageForm.get('main_image')?.setValue(this.selectedImage || '');
    this.gallery = data.gallery ?? [];
    this.editingPageId = data.id ?? null;

    if (data.id) {
      this.getGallery(data.id);
    }
  }

  getGallery(id: number) {
    this.PagesService.getAdminGallery(id, this.currentLang, 1).subscribe(
      (res: any) => {
        if (res?.status === 'success' && Array.isArray(res.items)) {
          this.galleryItems = res.items;
          this.splitMedia();

          const pinned = this.galleryItems.find((x: any) => Number(x.pin) === 1);
          if (pinned?.url) {
            this.selectedImage = pinned.url;
            this.pageForm.get('main_image')?.setValue(pinned.url);
          }

          const galleryImages = (this.imageItemsArr || [])
            .map((img: any) => img.url)
            .filter((u: any) => !!u);

          this.pageForm.patchValue({ gallery: galleryImages });
          this.gallery = galleryImages;
        }
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  onFilesSelected(files: string[]) {
    console.log('Selected files:', files);
    this.pageForm.get('gallery')?.setValue(files);
  }

  onImageSelect(imageUrl: string) {
    this.selectedImage = imageUrl;
    this.pageForm.get('main_image')?.setValue(imageUrl || '');
  }

  addToGallery(imageUrl: string) {
    if (!this.gallery.includes(imageUrl)) {
      this.gallery.push(imageUrl);
      this.pageForm.get('gallery')?.setValue([...this.gallery]);
    }
  }

  removeFromGallery(imageUrl: string) {
    this.gallery = this.gallery.filter(img => img !== imageUrl);
    this.pageForm.get('gallery')?.setValue([...this.gallery]);
  }

  removeGalleryImage(indexOrMediaId: number) {
    if (!this.editingPageId) return;

    const value = Number(indexOrMediaId);

    // 1) First try to treat the argument as media_id
    let item = (this.galleryItems || []).find(
      (x: any) => Number(x?.media_id) === value
    );

    // 2) If not found, fall back to treating it as an index from the images table
    if (!item && Number.isInteger(value) && value >= 0) {
      item = (this.imageItemsArr && this.imageItemsArr[value])
        ? this.imageItemsArr[value]
        : (this.galleryItems && this.galleryItems[value])
          ? this.galleryItems[value]
          : null;
    }

    console.log('removeGalleryImage resolved item:', item, 'arg:', indexOrMediaId);

    if (!item) return;

    this.PagesService.detachMedia({
      item_id: this.editingPageId,
      lang: this.currentLang,
      ref_type: 1,
      media_id: Number(item.media_id),
      delete_file: false // only detach from this post
    }).subscribe({
      next: () => {
        const mid = Number(item?.media_id);
        this.galleryItems = (this.galleryItems || []).filter(
          (x: any) => Number(x?.media_id) !== mid
        );
        this.splitMedia();
        this.rebuildFormGalleryFromItems();
      },
      error: (err: any) => {
        console.error('Detach media error:', err);
        this.ToastrService.error('სურათის წაშლა ვერ მოხერხდა');
      }
    });
  }

  dropGalleryImage(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.galleryItems, event.previousIndex, event.currentIndex);

    this.splitMedia();
    this.rebuildFormGalleryFromItems();

    // persist order
    this.persistSort();
  }

  // ---- split media into images/files (stable arrays for cdkDropList)
  private isImageMedia(x: any): boolean {
    const imgExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'];

    const extRaw = (x?.ext ?? x?.extension ?? '').toString().toLowerCase().trim();
    const mime = (x?.mime ?? x?.mimetype ?? '').toString().toLowerCase().trim();

    const name = String(x?.orig_name || x?.filename || x?.name || '').toLowerCase();
    const u = String(x?.url || x?.full_src || x?.src || x?.path || '').toLowerCase();

    // strip query/hash for ext detection
    const cleanUrl = u.split('?')[0].split('#')[0];
    const extFromUrl = cleanUrl.includes('.') ? (cleanUrl.split('.').pop() || '').toLowerCase() : '';
    const extFromName = name.includes('.') ? (name.split('.').pop() || '').toLowerCase() : '';

    // 1) If backend provides ext explicitly, trust it (most reliable)
    if (extRaw) {
      return imgExts.includes(extRaw);
    }

    // 2) If mime is present, trust it
    if (mime) {
      return mime.startsWith('image/');
    }

    // 3) If URL or filename has an extension, trust it
    if (extFromUrl) {
      return imgExts.includes(extFromUrl);
    }
    if (extFromName) {
      return imgExts.includes(extFromName);
    }

    // 4) Fallback by common image suffix in URL (when no clean ext)
    if (/(\.png|\.jpe?g|\.webp|\.gif|\.bmp|\.svg)(\?|#|$)/i.test(u)) {
      return true;
    }

    // 5) LAST resort: backend type (can be wrong sometimes)
    const t = x?.type;
    if (t === 1 || t === '1' || t === 'image') return true;
    if (t === 2 || t === '2' || t === 'file') return false;

    return false;
  }

  private splitMedia() {
    const all = Array.isArray(this.galleryItems) ? this.galleryItems : [];

    this.imageItemsArr = all.filter((x: any) => this.isImageMedia(x));
    this.videoItemsArr = all.filter((x: any) => !this.isImageMedia(x) && this.isVideoMedia(x));
    this.fileItemsArr = all.filter((x: any) => !this.isImageMedia(x) && !this.isVideoMedia(x));
  }

  private rebuildFormGalleryFromItems() {
    // only images go to `gallery` field (as you used before)
    const urls = (this.imageItemsArr || []).map((x: any) => x.url).filter((u: any) => !!u);
    this.gallery = urls;
    this.pageForm.get('gallery')?.setValue(urls);
  }

  private buildSortIds(): number[] {
    return (this.galleryItems || [])
      .map((x: any) => Number(x?.media_id))
      .filter((id: number) => Number.isFinite(id) && id > 0);
  }

  // სურათების reorder (მხოლოდ imageItems ლაგდება)
  dropImages(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.imageItemsArr, event.previousIndex, event.currentIndex);
    this.galleryItems = [...this.imageItemsArr, ...this.videoItemsArr, ...this.fileItemsArr];
    this.rebuildFormGalleryFromItems();
    this.persistSort();
  }

  // ფაილების reorder (მხოლოდ fileItems ლაგდება)
  dropFiles(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.fileItemsArr, event.previousIndex, event.currentIndex);
    this.galleryItems = [...this.imageItemsArr, ...this.videoItemsArr, ...this.fileItemsArr];
    this.rebuildFormGalleryFromItems();
    this.persistSort();
  }

  dropVideos(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.videoItemsArr, event.previousIndex, event.currentIndex);
    this.galleryItems = [...this.imageItemsArr, ...this.videoItemsArr, ...this.fileItemsArr];
    this.rebuildFormGalleryFromItems();
    this.persistSort();
  }

  // sort შენახვა ბექზე (ყველა media_id ერთიანი რიგით)
  private persistSort() {
    if (!this.editingPageId) return;

    const ids = this.buildSortIds();
    if (!ids.length) return;

    this.sorting = true;
    this.PagesService.sortMedia(
      this.editingPageId,
       this.currentLang,
      1,
      ids,
    ).subscribe({
      next: () => {
        this.sorting = false;
        // keep UI lists consistent
        this.splitMedia();
      },
      error: (err: any) => {
        console.error('Sort error:', err);
        this.sorting = false;
        this.ToastrService.error('სორტირება ვერ შეინახა');
      },
    });
  }

  onGalleryImagesSelected(images: string[]) {
    const current = this.pageForm.get('gallery')?.value || [];
    const merged = [...current, ...images.filter(img => !current.includes(img))];

    this.gallery = merged;
    this.pageForm.get('gallery')?.setValue(merged);
  }

  stripHtmlTags(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  addDomainToImageSrc(content: string): string {
    const domain = 'https://sca.ge';
    return content.replace(
      /<img([^>]+)src="(?!https?:\/\/|data:)([^"]+)"/g,
      (match, p1, p2) => {
        const fullSrc = p2.startsWith('/') ? `${domain}${p2}` : `${domain}/${p2}`;
        return `<img${p1}src="${fullSrc}"`;
      }
    );
  }

  decodeHtml(html: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  removeDomainFromImageSrc(content: string): string {
    return content.replace(/<img([^>]+)src="https?:\/\/[^\/]+(\/[^"]+)"/g, `<img$1src="$2"`);
  }

  onFileUploaded(res: any) {
    if (!this.editingPageId) return;

    // Support both response shapes:
    // old: { new: { path, url }, filename }
    // new: { path, url, filename }
    const path: string | undefined = res?.new?.path || res?.path;
    const fullSrc: string | undefined = res?.new?.url || res?.url || res?.new?.path || res?.path;
    const filename: string = res?.filename || res?.new?.filename || '';

    if (!path || !fullSrc) {
      console.error('Upload response missing path/url:', res);
      this.ToastrService.error('ატვირთულ ფაილზე ბმული ვერ მოიძებნა');
      return;
    }

    const ext = (filename.split('.').pop() || '').toLowerCase();
    const isImg = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(ext);
    const isVideo = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v'].includes(ext);

    const body = {
      item_id: this.editingPageId,
      lang: this.currentLang,
      ref_type: 1,
      src: path,
      full_src: fullSrc,
      orig_name: filename,
      ext,
      type: isImg ? 1 : (isVideo ? 3 : 2),
      pin: 0,
    };
    
    this.PagesService.attachMedia(body).subscribe({
      next: (r: any) => {
        if (r?.status === 'success') {
          this.getGallery(this.editingPageId as number);
        } else {
          console.error('Attach media unexpected response:', r);
          this.ToastrService.error('ფაილის მიბმა ვერ მოხერხდა');
        }
      },
      error: (err: any) => {
        console.error('Attach media error:', err);
        this.ToastrService.error('ფაილის მიბმის შეცდომა');
      },
    });
  }

  setMainImage(img: any) {
    if (!this.editingPageId || !img?.media_id) return;

    const body = {
      item_id: this.editingPageId,
      lang: this.currentLang,
      ref_type: 1,
      media_id: img.media_id,
    };

    this.PagesService.pinMedia(body).subscribe({
      next: () => {
        if (img?.url) {
          this.selectedImage = img.url;
          this.pageForm.get('main_image')?.setValue(img.url);
        }
        this.getGallery(this.editingPageId as number);
      },
      error: () => this.ToastrService.error('მთავარი სურათის დაყენება ვერ მოხერხდა')
    });
  }

  savePage() {
    const user = this.AdminService.user.name + ' ' + this.AdminService.user.lastname;
    this.pageForm.get('author')?.setValue(user);

    this.pageForm.patchValue({
      lang: this.currentLang,
      menu_id: this.menuid,
      gallery: this.gallery,
      main_image: this.selectedImage || ''
    });

    const wasNew = !this.editingPageId;
    const payload = {
      ...this.pageForm.value
    };

    this.loadSave = true;

    const finalize = (close: boolean) => {
      this.save.emit();
      // if (close && this.modalRef) {
      //   this.modalRef.close();
      // }
      this.loadSave = false;
    };

    this.PagesService.createUpdate(payload).subscribe({
      next: (res: any) => {
        if (res?.id) {
          this.editingPageId = res.id;
          this.pageForm.patchValue({ id: res.id });
        }

        if (wasNew) {
          this.ToastrService.success('შენახულია — ახლა შეგიძლია სურათების ატვირთვა');
          finalize(false);
        } else {
          this.ToastrService.success('შენახულია');
          finalize(true);
        }
      },
      error: () => {
        this.ToastrService.error('შეცდომა დამატებისას');
        this.loadSave = false;
      }
    });
  }

  getFullImageUrl(imagePath: string): string {
    if (!imagePath) return '';

    const raw = String(imagePath).trim();
    if (!raw) return '';

    // Old absolute URLs like https://api.sca.ge/storage/res/...
    // should now be served through Laravel route: /api/media/...
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      try {
        const url = new URL(raw);
        if (url.hostname === 'api.sca.ge' && url.pathname.startsWith('/storage/')) {
          const cleanPath = url.pathname.replace(/^\/storage\//, '');
          return `https://api.sca.ge/api/media/${cleanPath}`;
        }
        return raw;
      } catch {
        return raw;
      }
    }

    // Relative path like res/list/2026/03/file.webp
    const cleanPath = raw.replace(/^\/+/, '');
    return `https://api.sca.ge/api/media/${cleanPath}`;
  }

  isImage(m: any): boolean {
    const u = (m?.url || '').toLowerCase();
    return /\.(png|jpe?g|webp|gif)$/i.test(u) || m?.type === 'image';
  }

  // removeFileByMediaId(mediaId: number) {
  //   if (!this.editingPageId) return;

  //   const id = Number(mediaId);
  //   if (!Number.isFinite(id) || id <= 0) return;

  //   this.PagesService.detachMedia({
  //     item_id: this.editingPageId,
  //     lang: this.currentLang,
  //     ref_type: 1,
  //     media_id: id,
  //     delete_file: false,
  //   }).subscribe({
  //     next: () => {
  //       this.galleryItems = (this.galleryItems || []).filter((x: any) => Number(x?.media_id) !== id);
  //       this.splitMedia();
  //       this.rebuildFormGalleryFromItems();
  //     },
  //     error: (err: any) => {
  //       console.error('Detach file error:', err);
  //       this.ToastrService.error('ფაილის წაშლა ვერ მოხერხდა');
  //     },
  //   });
  // }


renameFile(file: any) {
  if (!this.editingPageId) return;
  if (!file || !file.media_id) return;

  const current = (file.title || file.orig_name || '').toString();
  const nextTitle = window.prompt('ფაილის სახელის შეცვლა', current);
  if (nextTitle === null) return;

  const title = nextTitle.trim();
  if (!title.length) {
    this.ToastrService.warning('სახელი ცარიელია');
    return;
  }

  const payload = {
    item_id: Number(this.editingPageId),
    lang: this.currentLang,
    ref_type: 1,
    media_id: Number(file.media_id),
    title,
  };

  this.PagesService.updateAdminMediaTitle(payload).subscribe({
    next: (res: any) => {
      if (res?.status && res.status !== 'success') {
        this.ToastrService.error(res?.message || 'შენახვა ვერ მოხერხდა');
        return;
      }

      const mid = Number(file.media_id);
      const apply = (arr: any[]) => {
        const idx = (arr || []).findIndex((x: any) => Number(x?.media_id) === mid);
        if (idx >= 0) arr[idx] = { ...arr[idx], title };
      };

      apply(this.galleryItems);
      apply(this.imageItemsArr);
      apply(this.fileItemsArr);

      this.ToastrService.success('შენახულია');
    },
    error: (err: any) => {
      console.error('updateAdminMediaTitle error:', err);
      this.ToastrService.error('შენახვა ვერ მოხერხდა');
    },
  });
}




  private isVideoMedia(x: any): boolean {
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v'];

  const extRaw = (x?.ext ?? x?.extension ?? '').toString().toLowerCase().trim();
  const mime = (x?.mime ?? x?.mimetype ?? '').toString().toLowerCase().trim();

  const name = String(x?.orig_name || x?.filename || x?.name || '').toLowerCase();
  const u = String(x?.url || x?.full_src || x?.src || x?.path || '').toLowerCase();

  const cleanUrl = u.split('?')[0].split('#')[0];
  const extFromUrl = cleanUrl.includes('.') ? (cleanUrl.split('.').pop() || '').toLowerCase() : '';
  const extFromName = name.includes('.') ? (name.split('.').pop() || '').toLowerCase() : '';

  if (extRaw) {
    return videoExts.includes(extRaw);
  }

  if (mime) {
    return mime.startsWith('video/');
  }

  if (extFromUrl) {
    return videoExts.includes(extFromUrl);
  }

  if (extFromName) {
    return videoExts.includes(extFromName);
  }

  if (/(\.mp4|\.webm|\.ogg|\.mov|\.avi|\.mkv|\.m4v)(\?|#|$)/i.test(u)) {
    return true;
  }

  return false;
}




}
